import { HttpErrorResponse } from '@angular/common/http';
import { Component, Inject, OnInit, PLATFORM_ID } from '@angular/core';
import { Router, ActivatedRoute } from '@angular/router';
import { ErrorStrings } from 'src/app/constants/form.constants';
import { ACCOUNT_TYPE_PREVIEW_ROUTE } from 'src/app/constants/route.constants';
import { Alert } from 'src/app/interfaces/alert';
import { AuthParams, UserAuthenticationType, AuthManagerService } from 'src/app/services/auth-manager/auth-manager.service';
import { RedirectionParams, RedirectionService } from 'src/app/services/redirection/redirection.service';
import { environment } from 'src/environments/environment';
import { isPlatformBrowser } from '@angular/common';
import { ConfigService } from 'src/app/services/config/config.service';
import { FeatureFlagsService } from 'src/app/services/feature-flag/feature-flag.service';
import { errorMessagesMap } from 'src/app/constants/error-messages';

@Component({
  selector: 'app-tok-login',
  templateUrl: './tok-login.component.html',
  styleUrls: ['./tok-login.component.scss']
})
export class TokLoginComponent implements OnInit {
  loading = false;
  alert: Alert = null;
  errorStrings = ErrorStrings;
  adminLoginUrl = environment.adminLoginUrl;
  redirectTimer = 0;
  redirectParams?: RedirectionParams;
  hidePassword: boolean = true;
  authWithChipNumber: boolean = false;
  isBrowser: boolean;
  companyPhoneNumber: string;
  // A keeper can only accept a ToK while on the premium plan, so the fee matches the premium annual price.
  tokFee: string;
  useRebrandedLayout: boolean;

  model: AuthParams = {
    identifier: '',
    password: '',
    authenticationType: UserAuthenticationType.Email,
  };

  constructor(
    public authManager: AuthManagerService,
    private router: Router,
    private route: ActivatedRoute,
    private redirectionService: RedirectionService,
    private configService: ConfigService,
    private featureFlagService: FeatureFlagsService,
    @Inject(PLATFORM_ID) private platformId: Object,
  ) {
    this.isBrowser = isPlatformBrowser(this.platformId);
    this.companyPhoneNumber = this.configService.getTextFromKey('company-phone-number');
    this.tokFee = this.configService.getTextFromKey('2-plan-yearly-price');
    this.useRebrandedLayout = this.featureFlagService.isEnabled('useRebrandedLayout');
  }
  
  ngOnInit(): void {
    if (this.authManager.isAuthenticated) {
      this.router.navigate(['/tok-details']);
    }

    // Prefill email address if "email" is passed as query param
    // (this is set when a redirection from the signup page - 409 status code)
    if (this.route.snapshot.queryParams.email) {
      this.model.identifier = this.route.snapshot.queryParams.email;
      this.alert = {
        type: 'success',
        message: `It looks like you're already registered. Please enter your password to log in.`,
        bold: true,
      };
    }

    // Setting redirection to be used when after login we want the user redirected to a specific page
    this.redirectParams = this.redirectionService.getParams(
      this.route.snapshot.queryParams.r
    );
  }

  login() {
    this.closeAlert();
    this.loading = true;
    this.trackEvent('login::clicked');
    this.model.authenticationType = UserAuthenticationType.Email;

    this.authManager.login(this.model).subscribe({
      next: (authToken) => {
        if (!authToken) {
          this.alert = {
            type: 'danger',
            message: 'You do not have permission to access this application.',
          };
          return;
        }
        // if the user comes from the account-type-preview page we want to redirect him to the dashboard
        if (
          this.redirectParams?.l &&
          !this.redirectParams.l.includes(`/${ACCOUNT_TYPE_PREVIEW_ROUTE}`)
        ) {
          this.router.navigate(this.redirectParams.l, {
            queryParams: this.redirectParams.q,
            fragment: this.redirectParams.f,
          });
        } else {
          this.router.navigate(['/tok-details']);
        }
      },
      error: (err) => {
        if (err instanceof HttpErrorResponse && err.status === 400) {
          if (
            err.error &&
            err.error.error_code === 'multiple_users_with_phone'
          ) {
            this.alert = {
              type: 'danger',
              message: err.error.error_message,
            };
          } else {
            this.alert = {
              type: 'danger',
              message:
                'Please enter a valid email address, phone number or chip number and a password.',
            };
          }
        } else if (err instanceof HttpErrorResponse && err.status === 401) {
            if (err.error && err.error.error_code === 'password_not_set') {
            // Legacy user with password not set
            this.router.navigate(['/forgot-password'], {
              queryParams: {
                email: this.model.identifier,
                type: 'passnotset',
              },
            });
          } else if (err.error && err.error.error_code === 'inactive_user') {
            this.alert = {
              type: 'danger',
              message: `Please contact identi on ${this.companyPhoneNumber}.`,
            };
          } else {
            this.alert = {
              type: 'danger',
              message:
                err.error?.message || errorMessagesMap.generic_error_message,
            };
          }
        } else if (err instanceof HttpErrorResponse && err.status === 423) {
          // Account temporarily locked after too many failed attempts
          this.alert = {
            type: 'danger',
            message: err.error?.message || errorMessagesMap.generic_error_message,
          };
        } else {
          this.alert = {
            type: 'danger',
            message: 'An error has occurred. Please try again later.',
          };
        }

        this.loading = false;
      },
      complete: () => {
        this.loading = false;
      }
    })
  }
  
  trackEvent(event: string) {
    if (!this.isBrowser) return;
    window.analytics.track(event);
  }

  displayAlert(alertText: string) {
    this.alert = {
      type: 'danger',
      message: alertText,
    };
  }
  closeAlert() {
    this.alert = null;
  }

}
